import React, { useState } from "react";
import { Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Theme } from "../../../providers/ThemeProvider";
import useTheme from "../../../common/hooks/useTheme";
import WalkthroughVideoPlayer from "../components/walkthrough/WalkthroughVideoPlayer";
import WalkthroughListItem from "../components/walkthrough/WalkthroughListItem";
import {
  formatDuration,
  getWalkthroughById,
  walkthroughs,
} from "../components/walkthrough/utils";
import { DescriptionBlock, Walkthrough } from "../components/walkthrough/types";

/**
 * Walkthrough detail screen (`/client/walkthroughs/:id`). Plays the selected
 * walkthrough on the left with its category, title, duration and a
 * collapsible description; the right column lists the remaining walkthroughs
 * so the user can jump to the next one.
 */
const WalkthroughDetailScreen = () => {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [expanded, setExpanded] = useState(false);

  const active = getWalkthroughById(id);

  // Unknown id (stale link / direct nav) — send back to the listing.
  if (!active) {
    return <Navigate to="/client/walkthroughs" replace />;
  }

  const others = walkthroughs.filter((w) => w.id !== active.id);

  const openWalkthrough = (walkthrough: Walkthrough) => {
    setExpanded(false);
    navigate(`/client/walkthroughs/${walkthrough.id}`);
  };

  const renderBlock = (block: DescriptionBlock, index: number) => {
    switch (block.type) {
      case "heading":
        return (
          <Box key={index} component="h4" sx={styles.descHeading(theme)}>
            {block.text}
          </Box>
        );
      case "list":
        return (
          <Box key={index} component="ul" sx={styles.descList(theme)}>
            {block.items.map((item, i) => (
              <Box key={i} component="li" sx={styles.descListItem}>
                {item}
              </Box>
            ))}
          </Box>
        );
      case "paragraph":
      default:
        return (
          <Box key={index} component="p" sx={styles.descParagraph(theme)}>
            {block.text}
          </Box>
        );
    }
  };

  return (
    <Box sx={styles.root}>
      {/* Left column: player + meta + description */}
      <Box sx={styles.mainColumn}>
        <WalkthroughVideoPlayer
          videoUrl={active.videoUrl}
          poster={active.thumbnail}
        />

        <Box sx={styles.meta}>
          <Box sx={styles.categoryPill(theme)}>{active.category}</Box>
          <Box sx={styles.title(theme)}>{active.title}</Box>
          <Box sx={styles.durationRow(theme)}>
            <AccessTimeIcon sx={styles.durationIcon(theme)} />
            <Box component="span">{formatDuration(active.durationMinutes)}</Box>
          </Box>
        </Box>

        {active.description && active.description.length > 0 && (
          <Box sx={styles.descriptionCard(theme)}>
            <Box sx={styles.descriptionContent(expanded)}>
              {active.description.map(renderBlock)}
            </Box>
            <Box
              sx={styles.showMore(theme)}
              onClick={() => setExpanded((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setExpanded((prev) => !prev);
              }}
            >
              <Box component="span">{expanded ? "Show less" : "Show more"}</Box>
              {expanded ? (
                <KeyboardArrowUpIcon sx={styles.showMoreIcon} />
              ) : (
                <KeyboardArrowDownIcon sx={styles.showMoreIcon} />
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Right column: more walkthroughs */}
      <Box sx={styles.sideColumn}>
        <Box sx={styles.sideTitle(theme)}>More Walkthroughs</Box>
        <Box sx={styles.sideList}>
          {others.map((walkthrough) => (
            <WalkthroughListItem
              key={walkthrough.id}
              walkthrough={walkthrough}
              onClick={openWalkthrough}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default WalkthroughDetailScreen;

const styles = {
  root: {
    display: "flex",
    gap: "24px",
    alignItems: "flex-start",
    width: "100%",
  },
  mainColumn: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
    flex: "1 1 780px",
    minWidth: 0,
  },
  meta: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: "4px",
  },
  categoryPill: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.P[10],
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    padding: "4px 8px",
    borderRadius: "16px",
    whiteSpace: "nowrap" as const,
  }),
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "25px",
  }),
  durationRow: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "27px",
  }),
  durationIcon: (theme: Theme) => ({
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  descriptionCard: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    borderRadius: "14px",
    backgroundColor: theme.colorTokens.N[30],
  }),
  descriptionContent: (expanded: boolean) => ({
    width: "100%",
    position: "relative" as const,
    overflow: "hidden",
    // Collapsed: cap the height and fade the cut-off edge; expanded: show all.
    maxHeight: expanded ? "none" : "180px",
    "&::after": expanded
      ? {}
      : {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "48px",
          background:
            "linear-gradient(to bottom, rgba(246,248,250,0), #F6F8FA)",
        },
  }),
  descParagraph: (theme: Theme) => ({
    margin: "0 0 12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "22.75px",
    letterSpacing: "-0.15px",
  }),
  descHeading: (theme: Theme) => ({
    margin: "16px 0 8px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  descList: (theme: Theme) => ({
    margin: "0 0 12px",
    paddingLeft: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "22.75px",
    letterSpacing: "-0.15px",
  }),
  descListItem: {
    marginBottom: "4px",
  },
  showMore: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "-0.15px",
  }),
  showMoreIcon: {
    fontSize: "16px",
  },
  sideColumn: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
    flex: "0 0 548px",
    maxWidth: "548px",
    width: "100%",
  },
  sideTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "27px",
    letterSpacing: "-0.44px",
  }),
  sideList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
};
